/**
 * Генератор случайных данных продаж для разработки и тестирования
 * ВНИМАНИЕ: Используется только в режиме разработки!
 */
import random from 'lodash/random'
import moment from 'moment'

/**
 * Генерирует случайную дату в заданном диапазоне
 * @param {Date} start - Начальная дата
 * @param {Date} end - Конечная дата
 * @returns {Date} Случайная дата
 */
const randomDate = (start, end) => {
  return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()))
}

/**
 * Генерирует случайные продажи для разработки
 * @param {Object} options - Опции генерации
 * @param {Number} options.count - Количество записей (по умолчанию 3000)
 * @param {Array} options.equipments - Массив оборудования
 * @param {Array} options.beverages - Массив напитков
 * @param {Array} options.clients - Массив клиентов
 * @param {Array} options.locations - Массив локаций
 * @param {Number} options.daysBack - Количество дней назад (по умолчанию 365)
 * @returns {Array} Массив сгенерированных продаж
 */
export const generateRandomSales = ({
  count = 3000,
  equipments = [],
  beverages = [],
  clients = [],
  locations = [],
  daysBack = 365
}) => {
  // Проверяем, что переданы необходимые данные
  if (!equipments.length || !beverages.length || !clients.length || !locations.length) {
    // eslint-disable-next-line no-console
    console.warn('generateRandomSales: Missing required data arrays')
    return []
  }

  const sales = []
  const now = new Date()
  const startDate = moment().subtract(daysBack, 'days').toDate()

  // Предварительно вычисляем совместимые напитки для каждого оборудования
  const beveragesByEquipment = new Map()
  for (const equipment of equipments) {
    if (equipment.ingredients && equipment.ingredients.length > 0) {
      const equipmentIngredientIds = new Set(equipment.ingredients.map((i) => i.id))
      const compatible = beverages.filter((beverage) => {
        const recipeIngredients = beverage.recipe?.recipeIngredients || []
        if (recipeIngredients.length === 0) return true
        return recipeIngredients.every((ri) =>
          equipmentIngredientIds.has(ri.ingredient?.id)
        )
      })
      beveragesByEquipment.set(equipment.id, compatible.length > 0 ? compatible : beverages)
    } else {
      beveragesByEquipment.set(equipment.id, beverages)
    }
  }

  for (let i = 1; i <= count; i++) {
    // Выбираем случайное оборудование
    const equipment = equipments[random(0, equipments.length - 1)]

    // Выбираем случайный напиток из совместимых для этого оборудования
    const compatibleBeverages = beveragesByEquipment.get(equipment.id) || beverages
    const beverageId = compatibleBeverages[random(0, compatibleBeverages.length - 1)].id

    // Выбираем случайного клиента и локацию
    const client = clients[random(0, clients.length - 1)]
    const location = locations[random(0, locations.length - 1)]

    // Генерируем случайную дату
    const timestamp = randomDate(startDate, now)

    // Находим напиток для получения цены (если есть)
    const beverage = beverages.find(b => b.id === beverageId)
    const price = beverage?.price || random(30, 150)

    // Создаем полные объекты вместо ID для совместимости с компонентами
    sales.push({
      id: i,
      timestamp: timestamp.toISOString(),
      // Полные объекты вместо ID
      equipment: equipment,
      beverage: beverage || { id: beverageId, title: `Beverage ${beverageId}` },
      client: client,
      location: location,
      // Также сохраняем ID для обратной совместимости
      equipmentId: equipment.id,
      beverageId: beverageId,
      clientId: client.id,
      locationId: location.id,
      amount: price,
      quantity: 1
    })
  }

  // Сортируем по дате (от старых к новым)
  return sales.sort((a, b) =>
    new Date(a.timestamp) - new Date(b.timestamp)
  )
}
